import { getAppSettings } from '@/appSettings';

// Parses `value` as a positive integer, otherwise returns `fallback`.
export function parsePositiveInt(value, fallback) {
  const parsed = parseInt(value, 10);
  return !isNaN(parsed) && parsed > 0 ? parsed : fallback;
}

// Parses a userId → limit override map from a JSON string or plain object.
// Drops entries with a non-numeric userId or a non-positive/non-numeric limit.
function parseUserLimitOverrides(raw) {
  let parsed = raw;
  if (typeof raw === 'string') {
    if (!raw) return {};
    try {
      parsed = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
  const result = {};
  Object.entries(parsed).forEach(([userId, limit]) => {
    const parsedUserId = parseInt(userId, 10);
    const parsedLimit = parseInt(limit, 10);
    if (!isNaN(parsedUserId) && !isNaN(parsedLimit) && parsedLimit > 0) {
      result[String(parsedUserId)] = parsedLimit;
    }
  });
  return result;
}

class Config {
  constructor() {
    this._nodeEnv = process.env.NODE_ENV;
  }

  /**
   * @param {boolean} value
   */
  set IsStandAlone(value) {
    this._isStandAlone = value;
  }

  get IsStandAlone() {
    return this._isStandAlone;
  }

  get NodeEnvironment() {
    return this._nodeEnv || process.env.NODE_ENV;
  }

  get MfeRootRoute() {
    return this._isStandAlone ? '/' : getAppSettings().MFE_ROOT_ROUTE;
  }

  // Build-time key — baked by DefinePlugin; must stay in sync with bob-cli's css write-key.
  get RootElementSelector() {
    return process.env.DEV_ROOT_ELEMENT_SELECTOR || 'token_management_ui';
  }

  // Build-time key — baked by DefinePlugin; must stay in sync with bob-cli's css write-key.
  get RootElementContainer() {
    return process.env.ROOT_ELEMENT_CONTAINER || 'shipbob_token_management_ui';
  }

  get AuthenticationAuthority() {
    return getAppSettings().AUTHENTICATION_AUTHORITY;
  }

  get ClientId() {
    return getAppSettings().CLIENT_ID;
  }

  get ClientScope() {
    return getAppSettings().CLIENT_SCOPE;
  }

  get Locale() {
    return getAppSettings().LOCALE || 'en-US';
  }

  get FallbackLocale() {
    return getAppSettings().FALLBACK_LOCALE || 'en-US';
  }

  get ExternalAuthBaseUrl() {
    return getAppSettings().EXTERNAL_AUTH_BASEURL;
  }

  get PatCreationEndpoint() {
    return getAppSettings().PAT_CREATION_ENDPOINT;
  }

  get GetPATDetailsEndpoint() {
    return getAppSettings().GET_PAT_DETAILS_ENDPOINT;
  }

  get ConfigEndpoint() {
    const s = getAppSettings();
    return s.CONFIG_ENDPOINT || s.PAT_CONFIG_ENDPOINT;
  }

  get PatRevokeEndpoint() {
    return getAppSettings().PAT_REVOKE_ENDPOINT;
  }

  get AppInsightsConnectionString() {
    return getAppSettings().APPINSIGHTS_CONNECTIONSTRING;
  }

  get IsTelemetryEnabled() {
    return !!this.AppInsightsConnectionString;
  }

  get AppCreationEndpoint() {
    return getAppSettings().APP_CREATION_ENDPOINT;
  }

  get GetAppDetailsEndpoint() {
    return getAppSettings().GET_APP_DETAILS_ENDPOINT;
  }

  get MarketPlaceAPIBaseURL() {
    return getAppSettings().MARKETPLACE_API_BASEURL;
  }

  get PublishAppEndpoint() {
    return getAppSettings().PUBLISH_APP_ENDPOINT;
  }

  get PublishAppDraftEndpoint() {
    return getAppSettings().PUBLISH_APP_DRAFT_ENDPOINT;
  }

  get UploadAppIconEndpoint() {
    return getAppSettings().UPLOAD_APP_ICON_ENDPOINT;
  }

  get ShipbobAppStoreUrl() {
    return getAppSettings().SHIPBOB_APP_STORE_URL;
  }

  get MarkAppPublishedEndpoint() {
    return getAppSettings().MARK_APP_PUBLISHED_ENDPOINT;
  }

  get CloseUpdateRequestEndpoint() {
    return getAppSettings().CLOSE_UPDATE_REQUEST_ENDPOINT;
  }

  get RegenerateSecretEndpoint() {
    return getAppSettings().REGENERATE_SECRET_ENDPOINT;
  }

  get RevokeSecretEndpoint() {
    return getAppSettings().REVOKE_SECRET_ENDPOINT;
  }

  get EditAppSettingsEndpoint() {
    return getAppSettings().EDIT_APP_SETTINGS_ENDPOINT;
  }

  get DeleteAppEndpoint() {
    return getAppSettings().DELETE_APP_ENDPOINT;
  }

  get AppPublishDetailsEndpoint() {
    return getAppSettings().GET_APP_PUBLISH_DETAILS_ENDPOINT;
  }

  get WebhooksBaseUrl() {
    return getAppSettings().WEBHOOKS_API_BASE_URL;
  }

  get ConsumerAppEndpoint() {
    return getAppSettings().CONSUMER_APP_ENDPOINT;
  }

  get MaxAllowedApps() {
    return getAppSettings().MAX_ALLOWED_APPS;
  }

  get MaxAllowedTokens() {
    const v = getAppSettings().MAX_ALLOWED_TOKENS;
    return v !== undefined && v !== null ? String(v) : v;
  }

  get MetricsApiCallStatsEndpoint() {
    return getAppSettings().METRICS_API_CALL_STATS_ENDPOINT;
  }

  get MetricsEndpointDetailsEndpoint() {
    return getAppSettings().METRICS_ENDPOINT_DETAILS_ENDPOINT;
  }

  get MetricsWebhookDeliveryStatsEndpoint() {
    return getAppSettings().METRICS_WEBHOOK_DELIVERY_STATS_ENDPOINT;
  }

  get MetricsWebhookDeliveryDetailsEndpoint() {
    return getAppSettings().METRICS_WEBHOOK_DELIVERY_DETAILS_ENDPOINT;
  }

  get MetricsInstallsStatsEndpoint() {
    return getAppSettings().METRICS_INSTALLS_STATS_ENDPOINT;
  }

  get MetricsInstallsEndpoint() {
    return getAppSettings().METRICS_INSTALLS_ENDPOINT;
  }

  get MetricsApiLogsEndpoint() {
    return getAppSettings().METRICS_API_LOGS_ENDPOINT;
  }

  get MetricsWebhookLogsEndpoint() {
    return getAppSettings().METRICS_WEBHOOK_LOGS_ENDPOINT;
  }

  get MetricsFilterEndpoint() {
    return getAppSettings().METRICS_FILTER_ENDPOINT;
  }

  get MetricsExportEndpoint() {
    return getAppSettings().METRICS_EXPORT_ENDPOINT;
  }

  get MetricsWebhookLogDetailsEndpoint() {
    return getAppSettings().METRICS_WEBHOOK_LOG_DETAILS_ENDPOINT;
  }

  get UsageGuideUrl() {
    return getAppSettings().APP_USAGE_GUIDE_URL;
  }

  get IsPatRoleRestrictionEnabled() {
    const value = getAppSettings().ENABLE_PAT_ROLE_RESTRICTION;
    return value === true || value === 'true';
  }

  get AllowedUsersForTokenGeneration() {
    const users = getAppSettings().ALLOWED_USERS_FOR_TOKEN_GENERATION;
    if (Array.isArray(users)) return users;
    return users ? users.split(',').map((email) => email.trim()) : [];
  }

  // Returns a map of userId → app limit overrides from USER_APP_LIMIT_OVERRIDES.
  // Format: JSON string (e.g. '{"12345":200}') or plain object from appsettings.json.
  // Keys align with shipbob_impersonating_user_id from token claims.
  get UserAppLimitOverrides() {
    return parseUserLimitOverrides(getAppSettings().USER_APP_LIMIT_OVERRIDES);
  }

  // Returns a map of userId → PAT limit overrides from USER_PAT_LIMIT_OVERRIDES.
  // Format: JSON string (e.g. '{"12345":200}') or plain object from appsettings.json.
  // Keys align with shipbob_impersonating_user_id from token claims.
  get UserPatLimitOverrides() {
    return parseUserLimitOverrides(getAppSettings().USER_PAT_LIMIT_OVERRIDES);
  }

  // Only for tests
  /**
   * @param {string} value
   */
  set NodeEnvironment(value) {
    this._nodeEnv = value;
  }
}

const configService = new Config();

export default configService;
